import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { researchService, ResearchStatus } from '../services/api';

const ResearchStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [research, setResearch] = useState<ResearchStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportReady, setReportReady] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);

  useEffect(() => {
    const fetchResearchStatus = async () => {
      try {
        if (!id) return;
        const data = await researchService.getResearchStatus(id);
        setResearch(data);
        
        // If research is complete, show notification instead of redirecting
        if (data.status === 'completed' && data.content) {
          setReportReady(true);
          setReportId(id);
          // Stop polling once report is ready
          return;
        }
        
        // If research is still in progress, poll for updates
        if (data.status === 'in_progress' || data.status === 'generating_outline') {
          const timer = setTimeout(() => {
            setLoading(true);
          }, 15000); // Poll every 15 seconds
          return () => clearTimeout(timer);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch research status');
      } finally {
        setLoading(false);
      }
    };

    if (loading) {
      fetchResearchStatus();
    }

    // Set up polling
    const interval = setInterval(() => {
      setLoading(true);
    }, 30000); // Poll every 30 seconds — research takes minutes

    return () => clearInterval(interval);
  }, [id, navigate, loading]);

  if (!id) {
    return <div className="text-center p-8">Invalid research ID</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const renderStatus = () => {
    if (!research) return null;

    switch (research.status) {
      case 'pending':
        return (
          <div className="bg-gray-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Preparing Research</h2>
            <p className="text-gray-700">
              {research.message || "We're preparing to start your research. This will begin shortly."}
            </p>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-gray-500 h-2.5 rounded-full w-1/6 animate-pulse"></div>
              </div>
            </div>
          </div>
        );

      case 'searching':
      case 'in_progress': // For backward compatibility
        return (
          <div className="bg-blue-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-blue-800 mb-2">Research in Progress</h2>
            <p className="text-blue-700">
              {research.message || "We're gathering information and conducting research on your topic. This may take a few minutes."}
            </p>
            <div className="mt-4">
              <div className="w-full bg-blue-200 rounded-full h-2.5">
                <div className="bg-blue-600 h-2.5 rounded-full w-1/3 animate-pulse"></div>
              </div>
            </div>
          </div>
        );
      
      case 'outlining':
      case 'generating_outline': // For backward compatibility
        return (
          <div className="bg-indigo-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-indigo-800 mb-2">Creating Research Outline</h2>
            <p className="text-indigo-700">
              {research.message || "We're organizing the research findings and creating a comprehensive outline."}
            </p>
            <div className="mt-4">
              <div className="w-full bg-indigo-200 rounded-full h-2.5">
                <div className="bg-indigo-600 h-2.5 rounded-full w-2/3 animate-pulse"></div>
              </div>
            </div>
          </div>
        );
      
      case 'writing':
      case 'generating_report': // For backward compatibility
        return (
          <div className="bg-purple-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-purple-800 mb-2">Generating Full Report</h2>
            <p className="text-purple-700">
              {research.message || "We're writing your comprehensive research report based on the outline."}
            </p>
            {research.outline && (
              <div className="mt-4 p-4 bg-white rounded shadow-sm">
                <h3 className="font-medium text-gray-800 mb-2">Research Outline:</h3>
                <p className="text-gray-700 whitespace-pre-line">{research.outline.title}</p>
                {research.outline.sections && (
                  <ul className="mt-2 list-disc pl-5">
                    {research.outline.sections.map((section: any, index: number) => (
                      <li key={index} className="text-gray-700">{section.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="mt-4">
              <div className="w-full bg-purple-200 rounded-full h-2.5">
                <div className="bg-purple-600 h-2.5 rounded-full w-5/6 animate-pulse"></div>
              </div>
            </div>
          </div>
        );
      
      case 'completed':
        return (
          <div className="bg-green-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-green-800 mb-2">Research Completed</h2>
            <p className="text-green-700">
              {research.message || "Your research report has been successfully generated and is ready to view."}
            </p>
            <div className="mt-4 flex space-x-4">
              <Link 
                to={`/reports/${id}`} 
                className="inline-flex items-center px-4 py-2 bg-primary-900 text-white text-sm font-medium rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-700"
              >
                View Full Report
              </Link>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="bg-red-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Research Failed</h2>
            <p className="text-red-700">
              {research.error || 'There was an error processing your research request.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        );
      
      default:
        return (
          <div className="bg-gray-100 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Unknown Status</h2>
            <p className="text-gray-700">
              The status of your research is unknown. Please try again later.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      {reportReady && reportId && (
        <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md" role="alert">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-bold">Your research report is ready!</p>
              <p className="text-sm">Your comprehensive report has been generated and is ready to view.</p>
            </div>
          </div>
          <div className="mt-3 flex space-x-4">
            <Link 
              to={`/reports/${reportId}`} 
              className="inline-flex items-center px-4 py-2 bg-primary-900 text-white text-sm font-medium rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-700"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Report
            </Link>
            <Link 
              to="/reports" 
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              All Reports
            </Link>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow-xl rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Research Status</h1>
        
        {loading && !research ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          renderStatus()
        )}
        
        <div className="mt-6 text-sm text-gray-500">
          <p>Research ID: {id}</p>
          {research && (
            <>
              <p>Status: {research.status}</p>
              <p>Topic: {research.topic}</p>
              <p>Created: {new Date(research.created_at).toLocaleString()}</p>
              <p>Last Updated: {new Date(research.updated_at).toLocaleString()}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResearchStatusPage;
